import React, { useEffect, useRef, useState } from 'react';
import { Ban, RefreshCw } from 'lucide-react';
import { formatDateTime } from '../../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../../shared/DateTimeProvider';
import { tenantApi, type BulkNotificationReplayItemResult, type BulkNotificationReplaySummary, type NotificationDeliveryAttempt, type NotificationDeliveryFailure, type NotificationDeliveryFailureFilters, type NotificationReplayOperation } from '../../services/tenantApi';
import './NotificationDeliveryQueue.css';

const PAGE_SIZE = 25;

export const NotificationDeliveryQueue: React.FC = () => {
  useDateTimePreferences();
  const [items, setItems] = useState<NotificationDeliveryFailure[]>([]);
  const [selected, setSelected] = useState<NotificationDeliveryFailure | null>(null);
  const [attempts, setAttempts] = useState<NotificationDeliveryAttempt[]>([]);
  const [operation, setOperation] = useState<NotificationReplayOperation | null>(null);
  const [skip, setSkip] = useState(0);
  const [filters, setFilters] = useState<NotificationDeliveryFailureFilters>({});
  const [draftStatus, setDraftStatus] = useState('');
  const [draftChannelType, setDraftChannelType] = useState('');
  const [draftProvider, setDraftProvider] = useState('');
  const [draftMailboxName, setDraftMailboxName] = useState('');
  const [draftTimeRange, setDraftTimeRange] = useState('all');
  const [selectedRequestIds, setSelectedRequestIds] = useState<Set<string>>(new Set());
  const [showBulkReplay, setShowBulkReplay] = useState(false);
  const [bulkReason, setBulkReason] = useState('');
  const [confirmBulkDuplicateRisk, setConfirmBulkDuplicateRisk] = useState(false);
  const [bulkResults, setBulkResults] = useState<BulkNotificationReplayItemResult[] | null>(null);
  const [bulkOperationStatusId, setBulkOperationStatusId] = useState<string | null>(null);
  const [bulkOperationState, setBulkOperationState] = useState<string | null>(null);
  const [recentBulkOperations, setRecentBulkOperations] = useState<BulkNotificationReplaySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showReplay, setShowReplay] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmDuplicateRisk, setConfirmDuplicateRisk] = useState(false);
  const [showCancellation, setShowCancellation] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [confirmCancellation, setConfirmCancellation] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectedId = useRef<string | null>(null);
  const detailGeneration = useRef(0);
  const operationGeneration = useRef(0);
  const listGeneration = useRef(0);
  const mutationInFlight = useRef(false);
  const bulkOperationId = useRef<string | null>(null);
  const bulkStatusGeneration = useRef(0);
  const recentBulkGeneration = useRef(0);

  const load = async (pageSkip = skip, queryFilters = filters) => {
    const generation = ++listGeneration.current;
    setLoading(true);
    setError(null);
    try {
      const result = await tenantApi.getNotificationDeliveryFailures(pageSkip, PAGE_SIZE, queryFilters);
      if (generation === listGeneration.current) setItems(result);
    } catch (loadError) {
      if (generation === listGeneration.current) setError(loadError instanceof Error ? loadError.message : 'Failed to load delivery failures');
    } finally {
      if (generation === listGeneration.current) setLoading(false);
    }
  };

  const loadRecentBulkOperations = async () => {
    const generation = ++recentBulkGeneration.current;
    try {
      const operations = await tenantApi.getRecentBulkNotificationDeliveryReplays(10);
      if (generation === recentBulkGeneration.current) setRecentBulkOperations(operations);
    } catch {
      if (generation === recentBulkGeneration.current) setRecentBulkOperations([]);
    }
  };

  useEffect(() => {
    void load(0);
    void loadRecentBulkOperations();
    return () => {
      selectedId.current = null;
      detailGeneration.current++;
      operationGeneration.current++;
      listGeneration.current++;
      bulkStatusGeneration.current++;
      recentBulkGeneration.current++;
    };
  }, []);

  const applyFilters = (event: React.FormEvent) => {
    event.preventDefault();
    const now = new Date();
    const rangeMilliseconds = draftTimeRange === '24h' ? 24 * 60 * 60 * 1000
      : draftTimeRange === '7d' ? 7 * 24 * 60 * 60 * 1000 : null;
    const nextFilters: NotificationDeliveryFailureFilters = {
      status: draftStatus || undefined,
      channelType: draftChannelType || undefined,
      deliveryProvider: draftProvider.trim() || undefined,
      mailboxName: draftMailboxName.trim() || undefined,
      updatedAfterUtc: rangeMilliseconds === null ? undefined : new Date(now.getTime() - rangeMilliseconds).toISOString(),
      updatedBeforeUtc: rangeMilliseconds === null ? undefined : now.toISOString(),
    };
    setFilters(nextFilters);
    setSkip(0);
    setSelectedRequestIds(new Set());
    setBulkResults(null);
    setBulkOperationStatusId(null);
    setBulkOperationState(null);
    bulkOperationId.current = null;
    void load(0, nextFilters);
  };

  const resetFilters = () => {
    setDraftStatus('');
    setDraftChannelType('');
    setDraftProvider('');
    setDraftMailboxName('');
    setDraftTimeRange('all');
    setFilters({});
    setSkip(0);
    setSelectedRequestIds(new Set());
    setBulkResults(null);
    setBulkOperationStatusId(null);
    setBulkOperationState(null);
    bulkOperationId.current = null;
    void load(0, {});
  };

  const toggleRequestSelection = (requestId: string, selectedValue: boolean) => {
    setSelectedRequestIds((previous) => {
      const next = new Set(previous);
      if (selectedValue) next.add(requestId);
      else next.delete(requestId);
      return next;
    });
    setBulkResults(null);
    setBulkOperationStatusId(null);
    setBulkOperationState(null);
    bulkStatusGeneration.current++;
  };

  const submitBulkReplay = async (event: React.FormEvent) => {
    event.preventDefault();
    const requestIds = Array.from(selectedRequestIds);
    if (!requestIds.length || !bulkReason.trim() || !confirmBulkDuplicateRisk || mutationInFlight.current) return;
    const operationId = bulkOperationId.current ?? crypto.randomUUID();
    bulkOperationId.current = operationId;
    mutationInFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const response = await tenantApi.requestBulkNotificationDeliveryReplay({
        operationId, requestIds, reason: bulkReason.trim(), confirmDuplicateRisk: confirmBulkDuplicateRisk,
      });
      setBulkResults(response.items);
      setBulkOperationStatusId(response.operationId);
      setBulkOperationState(response.state);
      setSelectedRequestIds(new Set());
      setShowBulkReplay(false);
      setBulkReason('');
      setConfirmBulkDuplicateRisk(false);
      bulkOperationId.current = null;
      await load();
      await loadRecentBulkOperations();
    } catch (bulkError) {
      setError(bulkError instanceof Error ? bulkError.message : 'Failed to request bulk resubmission');
    } finally {
      mutationInFlight.current = false;
      setSubmitting(false);
    }
  };

  const refreshBulkOperation = async () => {
    if (!bulkOperationStatusId) return;
    const operationId = bulkOperationStatusId;
    const generation = ++bulkStatusGeneration.current;
    try {
      const response = await tenantApi.getBulkNotificationDeliveryReplay(operationId);
      if (generation !== bulkStatusGeneration.current || operationId !== bulkOperationStatusId) return;
      setBulkResults(response.items);
      setBulkOperationState(response.state);
      await loadRecentBulkOperations();
    } catch (refreshError) {
      if (generation === bulkStatusGeneration.current)
        setError(refreshError instanceof Error ? refreshError.message : 'Failed to refresh bulk replay operation');
    }
  };

  const openRecentBulkOperation = async (operationId: string) => {
    const generation = ++bulkStatusGeneration.current;
    setBulkOperationStatusId(operationId);
    setBulkResults([]);
    setBulkOperationState('Loading');
    setError(null);
    try {
      const response = await tenantApi.getBulkNotificationDeliveryReplay(operationId);
      if (generation !== bulkStatusGeneration.current) return;
      setBulkOperationState(response.state);
      setBulkResults(response.items);
    } catch (loadError) {
      if (generation === bulkStatusGeneration.current)
        setError(loadError instanceof Error ? loadError.message : 'Failed to load bulk operation status');
    }
  };

  const resumeRecentBulkOperation = async (operationId: string) => {
    if (mutationInFlight.current) return;
    const generation = ++bulkStatusGeneration.current;
    mutationInFlight.current = true;
    setSubmitting(true);
    setBulkOperationStatusId(operationId);
    setBulkResults([]);
    setBulkOperationState('Resuming');
    setError(null);
    try {
      const response = await tenantApi.resumeBulkNotificationDeliveryReplay(operationId);
      if (generation !== bulkStatusGeneration.current) return;
      setBulkOperationState(response.state);
      setBulkResults(response.items);
      await loadRecentBulkOperations();
    } catch (resumeError) {
      if (generation === bulkStatusGeneration.current)
        setError(resumeError instanceof Error ? resumeError.message : 'Failed to resume bulk replay operation');
    } finally {
      mutationInFlight.current = false;
      setSubmitting(false);
    }
  };

  const openDetail = async (failure: NotificationDeliveryFailure) => {
    const generation = ++detailGeneration.current;
    selectedId.current = failure.requestId;
    operationGeneration.current++;
    setSelected(failure);
    setAttempts([]);
    setOperation(null);
    setShowReplay(false);
    setReason('');
    setConfirmDuplicateRisk(false);
    setShowCancellation(false);
    setCancellationReason('');
    setConfirmCancellation(false);
    setDetailLoading(true);
    setError(null);
    try {
      const [detail, history] = await Promise.all([
        tenantApi.getNotificationDelivery(failure.requestId),
        tenantApi.getNotificationDeliveryAttempts(failure.requestId, 0, 50),
      ]);
      if (generation !== detailGeneration.current) return;
      const latestOperation = detail.latestReplayCommandId
        ? await tenantApi.getNotificationReplayOperation(detail.latestReplayCommandId) : null;
      if (generation !== detailGeneration.current) return;
      setSelected(detail);
      setAttempts(history);
      setOperation(latestOperation);
    } catch (detailError) {
      if (generation !== detailGeneration.current) return;
      setError(detailError instanceof Error ? detailError.message : 'Failed to load delivery history');
      setAttempts([]);
    } finally {
      if (generation === detailGeneration.current) setDetailLoading(false);
    }
  };

  const submitReplay = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected || !reason.trim() || !confirmDuplicateRisk || mutationInFlight.current) return;
    const requestId = selected.requestId;
    const generation = ++operationGeneration.current;
    mutationInFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const accepted = await tenantApi.requestNotificationDeliveryReplay(selected.requestId, {
        idempotencyKey: crypto.randomUUID(),
        reason: reason.trim(),
        confirmDuplicateRisk,
      });
      const status = await tenantApi.getNotificationReplayOperation(accepted.replayCommandId);
      if (selectedId.current !== requestId || generation !== operationGeneration.current) return;
      setOperation(status);
      setShowReplay(false);
      setReason('');
      setConfirmDuplicateRisk(false);
      await load();
    } catch (replayError) {
      if (selectedId.current === requestId && generation === operationGeneration.current)
        setError(replayError instanceof Error ? replayError.message : 'Failed to request replay');
    } finally {
      mutationInFlight.current = false;
      if (selectedId.current === requestId) setSubmitting(false);
    }
  };

  const refreshOperation = async () => {
    if (!operation || mutationInFlight.current) return;
    const requestId = selectedId.current;
    const generation = ++operationGeneration.current;
    try {
      const status = await tenantApi.getNotificationReplayOperation(operation.replayCommandId);
      if (selectedId.current === requestId && generation === operationGeneration.current) {
        setOperation(status);
        if (status.state !== 'Pending') setShowCancellation(false);
      }
    } catch (refreshError) {
      if (selectedId.current === requestId && generation === operationGeneration.current)
        setError(refreshError instanceof Error ? refreshError.message : 'Failed to refresh replay status');
    }
  };

  const cancelReplay = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!operation || operation.state !== 'Pending' || !cancellationReason.trim() || !confirmCancellation || mutationInFlight.current) return;
    const requestId = selectedId.current;
    const generation = ++operationGeneration.current;
    mutationInFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      await tenantApi.cancelNotificationReplayOperation(operation.replayCommandId, {
        expectedPublishAttemptCount: operation.publishAttemptCount, reason: cancellationReason.trim(),
      });
      if (selectedId.current !== requestId || generation !== operationGeneration.current) return;
      setOperation({ ...operation, state: 'Cancelled' });
      setShowCancellation(false);
      setCancellationReason('');
      setConfirmCancellation(false);
      const status = await tenantApi.getNotificationReplayOperation(operation.replayCommandId);
      if (selectedId.current !== requestId || generation !== operationGeneration.current) return;
      setOperation(status);
      await load();
    } catch (cancelError) {
      if (selectedId.current !== requestId || generation !== operationGeneration.current) return;
      setError(cancelError instanceof Error ? cancelError.message : 'Failed to cancel pending replay');
      try {
        const status = await tenantApi.getNotificationReplayOperation(operation.replayCommandId);
        if (selectedId.current === requestId && generation === operationGeneration.current) {
          setOperation(status);
          if (status.state !== 'Pending') setShowCancellation(false);
        }
      } catch {
        return;
      }
    } finally {
      mutationInFlight.current = false;
      if (selectedId.current === requestId) setSubmitting(false);
    }
  };

  return (
    <section className="notification-delivery-queue">
      <div className="section-header">
        <div>
          <h2>Failed email deliveries</h2>
        </div>
        <button className="btn-secondary" type="button" disabled={loading} onClick={() => void load()}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {error && <div className="error-message" role="alert">{error}</div>}
      <form className="delivery-queue-filters" aria-label="Filter failed deliveries" onSubmit={applyFilters}>
        <label>Status
          <select aria-label="Delivery status filter" value={draftStatus} onChange={(event) => setDraftStatus(event.target.value)}>
            <option value="">All statuses</option>
            <option value="Failed">Failed</option>
            <option value="PartialSuccess">Partial success</option>
            <option value="Error">Error</option>
          </select>
        </label>
        <label>Channel
          <select aria-label="Delivery channel filter" value={draftChannelType} onChange={(event) => setDraftChannelType(event.target.value)}>
            <option value="">All channels</option>
            <option value="Email">Email</option>
            <option value="SMS">SMS</option>
            <option value="Teams">Teams</option>
            <option value="Webhook">Webhook</option>
          </select>
        </label>
        <label>Provider
          <input aria-label="Delivery provider filter" value={draftProvider} maxLength={100} onChange={(event) => setDraftProvider(event.target.value)} />
        </label>
        <label>Mailbox
          <input aria-label="Delivery mailbox filter" value={draftMailboxName} maxLength={200} onChange={(event) => setDraftMailboxName(event.target.value)} />
        </label>
        <label>Updated
          <select aria-label="Delivery time filter" value={draftTimeRange} onChange={(event) => setDraftTimeRange(event.target.value)}>
            <option value="all">Any time</option>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
          </select>
        </label>
        <div className="delivery-filter-actions">
          <button className="btn-primary" type="submit" disabled={loading}>Apply filters</button>
          <button className="btn-secondary" type="button" disabled={loading} onClick={resetFilters}>Reset</button>
        </div>
      </form>
      {loading ? <div className="loading">Loading failed deliveries...</div> : (
        <>
          <div className="delivery-queue-table-wrap">
            <table className="data-table delivery-queue-table">
              <thead><tr><th>Updated</th><th>Status</th><th>Channel / provider</th><th>Attempts</th><th>Latest error</th><th>Select</th><th>Detail</th></tr></thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.requestId} aria-selected={selected?.requestId === item.requestId}>
                    <td>{formatDateTime(item.updatedAt)}</td>
                    <td>{item.status}{item.latestReplayState && <div>Replay {item.latestReplayState}</div>}</td>
                    <td>
                      {[item.lastChannelType, item.lastDeliveryProvider].filter(Boolean).join(' · ') || 'Email'}
                      {item.lastMailboxName && <div>{item.lastMailboxName}</div>}
                    </td>
                    <td>{item.attemptCount}</td>
                    <td>{[item.lastErrorClass, item.lastErrorCode].filter(Boolean).join(' · ') || 'Unknown'}</td>
                    <td><input type="checkbox" aria-label={`Select failed delivery ${item.requestId}`} checked={selectedRequestIds.has(item.requestId)} disabled={submitting} onChange={(event) => toggleRequestSelection(item.requestId, event.target.checked)} /></td>
                    <td><button className="btn-secondary" type="button" disabled={submitting} onClick={() => void openDetail(item)}>View</button></td>
                  </tr>
                ))}
                {!items.length && <tr><td colSpan={7} className="empty-row">No failed email deliveries.</td></tr>}
              </tbody>
            </table>
          </div>
          {selectedRequestIds.size > 0 && !showBulkReplay && (
            <div className="delivery-bulk-selection" role="status">
              <span>{selectedRequestIds.size} selected</span>
              <button className="btn-primary" type="button" disabled={submitting} onClick={() => setShowBulkReplay(true)}>Request selected resubmission</button>
              <button className="btn-secondary" type="button" disabled={submitting} onClick={() => setSelectedRequestIds(new Set())}>Clear selection</button>
            </div>
          )}
          {bulkResults && (
            <div className="bulk-replay-results" role="status" aria-label="Bulk replay results">
              <strong>Bulk resubmission {bulkOperationState || 'results'}</strong>
              {bulkOperationStatusId && <span> · {bulkOperationStatusId}</span>}
              {bulkOperationState !== 'Completed' && bulkOperationStatusId && (
                <button className="btn-secondary" type="button" aria-label="Refresh bulk replay status" title="Refresh bulk replay status" onClick={() => void refreshBulkOperation()}>
                  <RefreshCw size={16} aria-hidden="true" />
                </button>
              )}
              <ul>{bulkResults.map((item) => <li key={item.requestId}>{item.requestId}: {item.outcome}{item.replayCommandId ? ` (${item.state || 'Pending'})` : ''}</li>)}</ul>
            </div>
          )}
          {showBulkReplay && (
            <form className="delivery-replay-form" aria-label="Request selected resubmission" onSubmit={(event) => void submitBulkReplay(event)}>
              <strong>Resubmit {selectedRequestIds.size} selected deliveries</strong>
              <label>Reason for resubmission
                <textarea value={bulkReason} onChange={(event) => setBulkReason(event.target.value)} maxLength={500} rows={3} required disabled={submitting} />
              </label>
              <label className="delivery-risk-confirmation">
                <input type="checkbox" checked={confirmBulkDuplicateRisk} onChange={(event) => setConfirmBulkDuplicateRisk(event.target.checked)} disabled={submitting} />
                I reviewed these deliveries and understand resubmission can create duplicates.
              </label>
              <div className="form-actions">
                <button className="btn-secondary" type="button" disabled={submitting} onClick={() => setShowBulkReplay(false)}>Cancel</button>
                <button className="btn-primary" type="submit" disabled={submitting || !bulkReason.trim() || !confirmBulkDuplicateRisk}>
                  {submitting ? 'Submitting...' : 'Confirm selected resubmission'}
                </button>
              </div>
            </form>
          )}
          <div className="delivery-queue-pagination">
            <span>{items.length ? `${skip + 1}-${skip + items.length}` : '0'} · newest first</span>
            <div>
              <button className="btn-secondary" type="button" disabled={skip === 0 || loading}
                onClick={() => { const next = Math.max(0, skip - PAGE_SIZE); setSkip(next); void load(next); }}>Previous</button>
              <button className="btn-secondary" type="button" disabled={items.length < PAGE_SIZE || loading}
                onClick={() => { const next = skip + PAGE_SIZE; setSkip(next); void load(next); }}>Next</button>
            </div>
          </div>
          {recentBulkOperations.length > 0 && (
            <section className="recent-bulk-operations" aria-label="Recent bulk resubmissions">
              <h3>Recent bulk resubmissions</h3>
              <ul>{recentBulkOperations.map((recent) => (
                <li key={recent.operationId}>
                  <button className="btn-secondary" type="button" aria-label={`Open bulk replay operation ${recent.operationId}`} onClick={() => void openRecentBulkOperation(recent.operationId)}>
                    {recent.operationId} · {recent.state} · {recent.itemCount} items
                  </button>
                  {recent.state === 'Processing' && <button className="btn-secondary" type="button" aria-label={`Resume bulk replay operation ${recent.operationId}`} disabled={submitting} onClick={() => void resumeRecentBulkOperation(recent.operationId)}>Resume</button>}
                </li>
              ))}</ul>
            </section>
          )}
        </>
      )}

      {selected && (
        <section className="delivery-detail-panel" aria-label="Delivery detail">
          <div className="delivery-detail-heading">
            <div>
              <h3>Delivery detail</h3>
              <span>{selected.requestId}</span>
            </div>
            <button className="btn-secondary" type="button" disabled={submitting} onClick={() => {
              selectedId.current = null; detailGeneration.current++; operationGeneration.current++;
              setSelected(null); setAttempts([]); setOperation(null); setShowCancellation(false);
            }}>Close</button>
          </div>
          {detailLoading ? <div className="loading">Loading attempt history...</div> : (
            <>
              <dl className="delivery-detail-grid">
                <div><dt>Status</dt><dd>{selected.status}</dd></div>
                <div><dt>Reason</dt><dd>{selected.dispatchReason || 'Not recorded'}</dd></div>
                <div><dt>Source</dt><dd>{selected.dispatchSource || 'Not recorded'}</dd></div>
                <div><dt>Attempts</dt><dd>{selected.attemptCount}</dd></div>
                <div><dt>Provider correlation</dt><dd>{selected.lastProviderCorrelationId || 'Not available'}</dd></div>
                <div><dt>Latest outcome</dt><dd>{[selected.lastAttemptStatus, selected.lastErrorClass, selected.lastErrorCode].filter(Boolean).join(' · ') || 'Not recorded'}</dd></div>
              </dl>

              <h4>Attempt history</h4>
              <div className="delivery-queue-table-wrap">
                <table className="data-table delivery-queue-table">
                  <thead><tr><th>Attempt</th><th>Channel</th><th>Provider</th><th>Status</th><th>Error</th><th>Correlation</th><th>Time</th></tr></thead>
                  <tbody>
                    {attempts.map((attempt) => (
                      <tr key={attempt.attemptId}>
                        <td>{attempt.attemptNumber}</td><td>{attempt.channelType}</td><td>{attempt.deliveryProvider || 'Unknown'}</td>
                        <td>{attempt.status}</td><td>{[attempt.errorClass, attempt.errorCode].filter(Boolean).join(' · ') || '—'}</td>
                        <td>{attempt.providerCorrelationId || '—'}</td><td>{formatDateTime(attempt.createdAt)}</td>
                      </tr>
                    ))}
                    {!attempts.length && <tr><td colSpan={7} className="empty-row">No attempts recorded.</td></tr>}
                  </tbody>
                </table>
              </div>

              {operation && (
                <div className="replay-operation-status" role="status">
                  <strong>Replay {operation.state}</strong>
                  <span> · publish attempts {operation.publishAttemptCount}</span>
                  {operation.lastErrorCode && <span> · {operation.lastErrorCode}</span>}
                  <button className="btn-secondary" type="button" disabled={submitting} title="Refresh replay status" aria-label="Refresh replay status" onClick={() => void refreshOperation()}><RefreshCw size={16} aria-hidden="true" /></button>
                  {operation.state === 'Pending' && !showCancellation && (
                    <button className="btn-secondary" type="button" disabled={submitting} onClick={() => setShowCancellation(true)}><Ban size={16} aria-hidden="true" /> Cancel replay</button>
                  )}
                </div>
              )}
              {operation?.state === 'Cancelled' && (
                <dl className="delivery-detail-grid">
                  <div><dt>Cancellation reason</dt><dd>{operation.cancellationReason || 'Not recorded'}</dd></div>
                  <div><dt>Cancelled at</dt><dd>{operation.cancelledAt ? formatDateTime(operation.cancelledAt) : 'Not recorded'}</dd></div>
                  <div><dt>Cancelled by</dt><dd>{operation.cancelledById || 'Not recorded'}</dd></div>
                </dl>
              )}
              {operation?.snapshotAvailable !== undefined && (
                <p className="delivery-snapshot-retention" role="note">
                  {operation.snapshotAvailable
                    ? `Replay data retained until ${operation.snapshotExpiresAt ? formatDateTime(operation.snapshotExpiresAt) : 'an unreported expiry'}.`
                    : 'Replay data is no longer available.'}
                </p>
              )}
              {showCancellation && operation?.state === 'Pending' && (
                <form className="delivery-replay-form" aria-label="Cancel pending replay" onSubmit={(event) => void cancelReplay(event)}>
                  <label>Reason for cancellation
                    <textarea value={cancellationReason} onChange={(event) => setCancellationReason(event.target.value)} maxLength={500} rows={3} required disabled={submitting} />
                  </label>
                  <label className="delivery-risk-confirmation">
                    <input type="checkbox" checked={confirmCancellation} onChange={(event) => setConfirmCancellation(event.target.checked)} disabled={submitting} />
                    I confirm cancellation of this pending replay.
                  </label>
                  <div className="form-actions">
                    <button className="btn-secondary" type="button" disabled={submitting} onClick={() => setShowCancellation(false)}>Keep replay</button>
                    <button className="btn-primary" type="submit" disabled={submitting || !cancellationReason.trim() || !confirmCancellation}>
                      {submitting ? 'Cancelling...' : 'Confirm cancellation'}
                    </button>
                  </div>
                </form>
              )}
              {['failed', 'error', 'partialsuccess'].includes(selected.status.toLowerCase()) && !showReplay && !error &&
                ((!operation && !selected.latestReplayCommandId) || operation?.state === 'Cancelled' || operation?.state === 'DeadLettered') && (
                <button className="btn-primary" type="button" onClick={() => setShowReplay(true)}>Request resubmission</button>
              )}
              {(selected.status === 'PartialSuccess' || selected.lastErrorClass?.toLowerCase() === 'unknown') && !operation && !showReplay && (
                <p className="delivery-duplicate-warning">This outcome may have sent already. Resubmission requires explicit duplicate-risk confirmation.</p>
              )}
              {showReplay && (
                <form className="delivery-replay-form" onSubmit={(event) => void submitReplay(event)}>
                  <label>
                    Reason for resubmission
                    <textarea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} rows={3} required />
                  </label>
                  <label className="delivery-risk-confirmation">
                    <input type="checkbox" checked={confirmDuplicateRisk} onChange={(event) => setConfirmDuplicateRisk(event.target.checked)} />
                    I reviewed this delivery and understand resubmission can create a duplicate notification.
                  </label>
                  <div className="form-actions">
                    <button className="btn-secondary" type="button" disabled={submitting} onClick={() => setShowReplay(false)}>Cancel</button>
                    <button className="btn-primary" type="submit" disabled={submitting || !reason.trim() || !confirmDuplicateRisk}>
                      {submitting ? 'Submitting...' : 'Confirm resubmission'}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </section>
      )}
    </section>
  );
};
